import { Component, inject } from '@angular/core';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatIcon } from '@angular/material/icon';
import { MatButton, MatIconButton } from '@angular/material/button';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { OrganizationGoal } from '../../model/organization/organization-goal.model';
import { OrganizationHeritageType } from '../../model/organization/organization-heritage-type.model';
import { OrganizationImpactType } from '../../model/organization/organization-impact-type.model';
import { OrganizationProvince } from '../../model/organization/organization-province.model';
import { OrganizationReach } from '../../model/organization/organization-reach.model';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { GoalService } from '../../services/goal.service';
import { HeritageTypeService } from '../../services/heritage-type.service';
import { ImpactTypeService } from '../../services/impact-type.service';
import { ProvinceService } from '../../services/province.service';
import { ReachService } from '../../services/reach.service';
import { debounceTime } from 'rxjs';
import { AppStateStore } from '../../store/app-state.store';
import { MatInput } from '@angular/material/input';
import { MatMenu, MatMenuContent, MatMenuTrigger } from '@angular/material/menu';
import { AsyncPipe } from '@angular/common';
import { GoalColor, getGoalColor } from '../../util/goal-color.util';

@Component({
  selector: 'app-filters-bottom-sheet',
  imports: [
    MatIcon,
    MatIconButton,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
    ReactiveFormsModule,
    MatButton,
    MatInput,
    MatMenu,
    MatMenuContent,
    MatMenuTrigger,
    AsyncPipe,
  ],
  templateUrl: './filters-bottom-sheet.html',
  styleUrl: './filters-bottom-sheet.scss',
})
export class FiltersBottomSheet {
  public sheet =
    inject<MatBottomSheetRef<FiltersBottomSheet>>(MatBottomSheetRef);
  public readonly appStateStore = inject(AppStateStore);
  private readonly goalService = inject(GoalService);
  private readonly heritageTypeService = inject(HeritageTypeService);
  private readonly impactTypeService = inject(ImpactTypeService);
  private readonly provinceService = inject(ProvinceService);
  private readonly reachService = inject(ReachService);
  private readonly STORAGE_KEY = 'nle-filters-state';

  public goalColor(goal: OrganizationGoal): GoalColor {
    return getGoalColor(goal.class);
  }

  /**
   * Filters
   */
    // Search query
  public searchQueryInput: FormControl = new FormControl();
  private searchQuery = '';
  // Goal filter
  public goalSelect = new FormControl<OrganizationGoal[] | null>([]);
  public goalList: OrganizationGoal[] = [];
  private selectedGoalIdList: string[] = [];
  // Heritage type filter
  public heritageTypeSelect = new FormControl<OrganizationHeritageType[] | null>([]);
  public heritageTypeList: OrganizationHeritageType[] = [];
  private selectedHeritageTypeIdList: string[] = [];
  // Impact type filter
  public impactTypeSelect = new FormControl<OrganizationImpactType[] | null>([]);
  public impactTypeList: OrganizationImpactType[] = [];
  private selectedImpactTypeIdList: string[] = [];
  // Province filter
  public provinceSelect = new FormControl<OrganizationProvince[] | null>([]);
  public provinceList: OrganizationProvince[] = [];
  private selectedProvinceIdList: string[] = [];
  // Reach filter
  public reachSelect = new FormControl<OrganizationReach[] | null>([]);
  public reachList: OrganizationReach[] = [];
  private selectedReachIdList: string[] = [];
  // Reset state
  private resetFiltersInProgress = false;

  constructor() {
    // Filters
    this.loadFiltersData();
    this.observeFilterChanges();
    this.restoreFiltersState();
  }

  /**
   * Filters
   */
  private loadFiltersData(): void {
    // Goal
    this.goalService.getGoalList()
      .subscribe((goalList: OrganizationGoal[]): OrganizationGoal[] => this.goalList = goalList);
    // Heritage type
    this.heritageTypeService.getHeritageTypeList()
      .subscribe((heritageTypeList: OrganizationHeritageType[]): OrganizationHeritageType[] => this.heritageTypeList = heritageTypeList);
    // Impact type
    this.impactTypeService.getImpactTypeList()
      .subscribe((impactTypeList: OrganizationImpactType[]): OrganizationImpactType[] => this.impactTypeList = impactTypeList);
    // Province
    this.provinceService.getProvinceList()
      .subscribe((provinceList: OrganizationProvince[]): OrganizationProvince[] => this.provinceList = provinceList);
    // Reach
    this.reachService.getReachList()
      .subscribe((reachList: OrganizationReach[]): OrganizationReach[] => this.reachList = reachList);
  }

  private observeFilterChanges(): void {
    // Search query
    this.searchQueryInput.valueChanges.pipe(debounceTime(200)).subscribe((value: string) => {
      this.searchQuery = value;
      this.loadStoryData();
    });
    // Goal
    this.goalSelect.valueChanges.subscribe((selectedGoalList: OrganizationGoal[] | null): void => {
      if (selectedGoalList !== null) {
        this.selectedGoalIdList = selectedGoalList.map((goal: OrganizationGoal) => goal.id);
        this.loadStoryData();
      }
    });
    // Heritage type
    this.heritageTypeSelect.valueChanges.subscribe((selectedHeritageTypeList: OrganizationHeritageType[] | null): void => {
      if (selectedHeritageTypeList !== null) {
        this.selectedHeritageTypeIdList = selectedHeritageTypeList.map((heritageType: OrganizationHeritageType) => heritageType.id);
        this.loadStoryData();
      }
    });
    // Impact type
    this.impactTypeSelect.valueChanges.subscribe((selectedImpactTypeList: OrganizationImpactType[] | null): void => {
      if (selectedImpactTypeList !== null) {
        this.selectedImpactTypeIdList = selectedImpactTypeList.map((impactType: OrganizationImpactType) => impactType.id);
        this.loadStoryData();
      }
    });
    // Province
    this.provinceSelect.valueChanges.subscribe((selectedProvinceList: OrganizationProvince[] | null): void => {
      if (selectedProvinceList !== null) {
        this.selectedProvinceIdList = selectedProvinceList.map((province: OrganizationProvince) => province.id);
        this.loadStoryData();
      }
    });
    // Reach
    this.reachSelect.valueChanges.subscribe((selectedReachList: OrganizationReach[] | null): void => {
      if (selectedReachList !== null) {
        this.selectedReachIdList = selectedReachList.map((reach: OrganizationReach) => reach.id);
        this.loadStoryData();
      }
    });
  }

  /**
   * Clear all filters
   */
  public clearFilters(): void {
    this.resetFiltersInProgress = true;
    this.searchQueryInput.reset('', {emitEvent: false});
    this.goalSelect.reset([], {emitEvent: false});
    this.heritageTypeSelect.reset([], {emitEvent: false});
    this.impactTypeSelect.reset([], {emitEvent: false});
    this.provinceSelect.reset([], {emitEvent: false});
    this.reachSelect.reset([], {emitEvent: false});
    this.resetFiltersInProgress = false;

    // Also reset the value holders
    this.searchQuery = '';
    this.selectedGoalIdList = [];
    this.selectedHeritageTypeIdList = [];
    this.selectedImpactTypeIdList = [];
    this.selectedProvinceIdList = [];
    this.selectedReachIdList = [];

    // Reload data
    this.loadStoryData();
  }

  public compareById(o1: { id: string }, o2: { id: string }): boolean {
    return o1?.id === o2?.id;
  }

  private saveFiltersState(): void {
    // Build the filter state to store
    const state = {
      searchQuery: this.searchQuery,
      selectedGoalIdList: this.selectedGoalIdList,
      selectedHeritageTypeIdList: this.selectedHeritageTypeIdList,
      selectedImpactTypeIdList: this.selectedImpactTypeIdList,
      selectedProvinceIdList: this.selectedProvinceIdList,
      selectedReachIdList: this.selectedReachIdList,
      goals: this.goalSelect.value,
      heritageTypes: this.heritageTypeSelect.value,
      impactTypes: this.impactTypeSelect.value,
      provinces: this.provinceSelect.value,
      reaches: this.reachSelect.value,
    };
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
  }

  private restoreFiltersState(): void {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (!stored) {
      return;
    }

    try {
      const state = JSON.parse(stored);

      // Restore internal state
      if (state.searchQuery) {
        this.searchQuery = state.searchQuery;
        this.searchQueryInput.setValue(state.searchQuery, {emitEvent: false});
      }

      if (state.selectedGoalIdList) {
        this.selectedGoalIdList = state.selectedGoalIdList;
      }
      if (state.goals) {
        this.goalSelect.setValue(state.goals, {emitEvent: false});
      }

      if (state.selectedHeritageTypeIdList) {
        this.selectedHeritageTypeIdList = state.selectedHeritageTypeIdList;
      }
      if (state.heritageTypes) {
        this.heritageTypeSelect.setValue(state.heritageTypes, {emitEvent: false});
      }

      if (state.selectedImpactTypeIdList) {
        this.selectedImpactTypeIdList = state.selectedImpactTypeIdList;
      }
      if (state.impactTypes) {
        this.impactTypeSelect.setValue(state.impactTypes, {emitEvent: false});
      }

      if (state.selectedProvinceIdList) {
        this.selectedProvinceIdList = state.selectedProvinceIdList;
      }
      if (state.provinces) {
        this.provinceSelect.setValue(state.provinces, {emitEvent: false});
      }

      if (state.selectedReachIdList) {
        this.selectedReachIdList = state.selectedReachIdList;
      }
      if (state.reaches) {
        this.reachSelect.setValue(state.reaches, {emitEvent: false});
      }

      // Initial load with restored filters
      this.loadStoryData();

    } catch (e) {
      console.error('Failed to restore filters state', e);
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  /**
   * Load filtered data from the back-end
   */
  public loadStoryData(): void {
    // Avoid loading when restoring the filters
    if (this.resetFiltersInProgress) {
      return;
    }
    // General filters and pagination
    const params: any = {
      search: this.searchQuery,
      'heritageGoals[]': this.selectedGoalIdList,
      'heritageTypes[]': this.selectedHeritageTypeIdList,
      'impactTypes[]': this.selectedImpactTypeIdList,
      'provinces[]': this.selectedProvinceIdList,
      'scopes[]': this.selectedReachIdList,
      active: true,
    };

    // Load data
    this.appStateStore.loadOrganizationListData({params});
    this.saveFiltersState();
  }
}
